import { useRef, useEffect, useState, useMemo, useReducer } from 'react';
import { importCSS, isDesktop, isNotEmpty } from './ep';

export const useOuterClick = (callback) => {
	const innerRef = useRef();
	const callbackRef = useRef();

	// set current callback in ref, before second useEffect uses it
	useEffect(() => {
		// useEffect wrapper to be safe for concurrent mode
		callbackRef.current = callback;
	});

	useEffect(() => {
		// read most recent callback and innerRef dom node from refs
		const handleClick = (e) => {
			if(innerRef.current && callbackRef.current && !innerRef.current.contains(e.target)) {
				callbackRef.current(e);
			}
		}

		document.addEventListener('click', handleClick);
		return () => document.removeEventListener('click', handleClick);
	}, []); // no need for callback + innerRef dep

	return innerRef; // return ref; client can omit `useRef`
};

export const useTouch = (props) => {
	const TOUCH_ENABLED = (document.querySelector('html').classList.contains('touch') || !isDesktop()) ? true : false;

	useEffect(() => {

		if(TOUCH_ENABLED) {
			document.addEventListener('touchstart', handleTouchStart, false);
			document.addEventListener('touchmove', handleTouchMove, false);
			document.addEventListener('touchend', handleTouchEnd, false);
		}

		return () => {

			if(TOUCH_ENABLED) {
				document.removeEventListener('touchstart', handleTouchStart, false);
				document.removeEventListener('touchmove', handleTouchMove, false);
				document.removeEventListener('touchend', handleTouchEnd, false);
			}
		}
	}, [props.enabled]);

	/* MOBILE TOUCH SWIPE DETECTION EVENTS - ini */
	// https://stackoverflow.com/questions/2264072/detect-a-finger-swipe-through-javascript-on-the-iphone-and-android
	let xDown = null;
	let yDown = null;

	const getTouches = (e) => {
		return e.touches || e.originalEvent.touches; // browser API || jQuery
	}

	const handleTouchStart = (e) => {
		if(props.enabled === false) return;

		const firstTouch = getTouches(e)[0];
		xDown = firstTouch.clientX;
		yDown = firstTouch.clientY;
	};

	const stopScrollPropagation = (e) => {
		e.stopPropagation();
	};

	const handleTouchMove = (e) => {
		if(props.enabled === false || !xDown || !yDown) return;

		const xUp = e.touches[0].clientX;
		const yUp = e.touches[0].clientY;

		const xDiff = xDown - xUp;
		const yDiff = yDown - yUp;

		if(Math.abs(xDiff) > Math.abs(yDiff)) {
			if(Math.max(Math.abs(xDiff), Math.abs(yDiff)) > 100) {
				if(xDiff > 0) {
					// left swipe
					if (props.leftSwipe != undefined) props.leftSwipe();
				} else {
					// right swipe
					if (props.rightSwipe != undefined) props.rightSwipe();
				}
				// reset values
				xDown = null;
				yDown = null;
			}

			stopScrollPropagation(e);
			return false;
		} else {
			if(yDiff > 0) {
				// up swipe
			} else {
				// down swipe
			}
		}
	};

	const handleTouchEnd = (e) => {
		// reset values
		xDown = null;
		yDown = null;
	}
	/* MOBILE TOUCH SWIPE DETECTION EVENTS - end */
};

export const useRankingWithSpan = (users, options) => {
	if(users === false) return [ false, false ];

	const { total_in_top, span, matching, getPos, filter_tags } = options;
	const max_results = options?.max_results ?? 1;
	let _users = users;
	if(filter_tags.length > 0){
		_users = users.filter((item) => {
			const user_tags = item?.Participation?.quiz_entry?.tags || [];
			return user_tags.filter((tag) => filter_tags.includes(tag)).length > 0;
		  });
	}
	const user_positions = (max_results == 1) ? [_users.find(matching)] : _users.filter(matching).slice(0, max_results);

	let top = _users.slice(0, parseInt(total_in_top));
	if(user_positions.length > 0 && getPos(user_positions[0]) < parseInt(total_in_top)) {
		return [ top, [] ,false ];
	}

	let gather_positions = {};
	for(var i in user_positions) {
		let index = parseInt(getPos(user_positions[i]));
		let separator = index - span - 1;
		if(gather_positions[separator] == undefined) gather_positions[separator] = 's';

		for(var j = span; j > 0; j--) {
			if(gather_positions[index - j] == undefined) gather_positions[index - j] = 1;
		}
		gather_positions[index] = 1;
		for(var j = span; j > 0; j--) {
			if(gather_positions[index + j] == undefined) gather_positions[index + j] = 1;
		}
	}

	let rest = [];

	for(var j in gather_positions) {
		let type = gather_positions[j];

		if(type == 's') {
			rest.push({ separator:1 });
			continue;
		}

		if(_users[j - 1] == undefined) continue;

		rest.push(_users[j - 1]);
	}
	return [ top, rest ];
};

export const useAutoSync = (last, fetchFunction, getID) => {
	const frequency = 5000;
	const focused = useRef(true);
	const timer = useRef(null);
	const last_id = useRef(false);
	const [ pending, setPending ] = useState([]);

	const setFocus = () => {
		focused.current = true;
		if(timer.current == false) fetchLast(last_id.current);
	};

	const setBlur = () => { focused.current = false; };
	const pendingProcessed = () => {
		setPending([]);
	};

	const fetchLast = (last) => {
		fetchFunction(last).then((data) => {
			let new_last = last;
			if(data.data.length > 0 ) {
				new_last = getID(data.data[0])
				setPending((pending) => ([ ...data.data, ...pending ]));
				last_id.current = new_last;
			};

			if(focused.current) {
				timer.current = setTimeout(() => { fetchLast(new_last) }, frequency);
			} else {
				clearTimeout(timer.current);
				timer.current = false;
			}
		})
	};

	useEffect(() => {
		if(timer.current) clearTimeout(timer.current);
		if(last === false) return;
		window.addEventListener('blur',setBlur);
		window.addEventListener('focus',setFocus);

		last_id.current = last;
		let initial = setTimeout(() => {
			fetchLast(last);
		}, frequency);

		return () => {
			window.removeEventListener('blur', setBlur);
			window.removeEventListener('focus', setFocus);
			clearTimeout(initial);
		};
	}, [ last ]);

	return [ pending, pendingProcessed ];
};

export const useImageFallback = (images) => {
	const index = useRef(0);
	const [ image, setImage ] = useState(images[0]);

	const checkImg = (url) => {
		const img = new Image();
		img.src = url;
		img.onload = imgLoaded;
		img.onerror = imgError;
	}

	const imgLoaded = () => {
		if(image != images[index.current]) {
			setImage(images[index.current]);
		}
	}

	const imgError = () => {
		index.current++;

		if(images[index.current] != undefined) {
			checkImg(images[index.current]);
		}
	};

	useEffect(() => {
		checkImg(images[index.current]);
	}, [ images ]);

	return image;
};

export function useThrottle (func, delay) {
	const [timeout, saveTimeout] = useState(null);
	  
	const throttledFunc = function () {
	  if (timeout) {
		clearTimeout(timeout);
	  }
  
	  const newTimeout = setTimeout(() => {
		func(...arguments);
		if (newTimeout === timeout) {
		  saveTimeout(null);
		}
	  }, delay);
  
	  saveTimeout(newTimeout);
	}
  
	return throttledFunc;
  }


// export const useThrottle = (func, delay, insta) => {
// 	const timer = useRef(undefined);
	
// 	const delayed = useCallback(() => {
// 		if(timer.current != undefined) clearTimeout(timer.current);
// 		if(insta != undefined && insta( ...arguments )) {
// 			func( ...arguments );
// 			return;
// 		}

// 		timer.current = setTimeout(() => {
// 			func( ...arguments );
// 		}, delay);
// 	}, [ func ]);

// 	return delayed;
// };

export const useCSS = (file) => {
	// we don't need it in useEffect, because the CSS file can be loaded even before the ReactJS component loads,
	// so we use useMemo instead of useEffect to execute it before the component mounts.
	useMemo(() => {
		importCSS(file);
	}, []);
};

export const useColorScheme = (onGetMode) => {
	const getActualColorScheme = () => {
		return (window.matchMedia('(prefers-color-scheme:dark)').matches) ? 'dark' : 'light';
	};

	const detectChange = (e) => {
		onGetMode((e.matches) ? 'dark' : 'light');
	};

	useEffect(() => {
		const darkMediaQuery = window.matchMedia('(prefers-color-scheme:dark)');

		try {
			// Chrome & Firefox
			darkMediaQuery.addEventListener('change', detectChange);
		} catch (e1) {
			try {
				// shitty Safari
				darkMediaQuery.addListener(detectChange);
			} catch (e2) {
				console.error(e2);
			}
		}

		// Setup dark/light mode for the first time
		onGetMode(getActualColorScheme());

		return () => {
			try {
				// Chrome & Firefox
				darkMediaQuery.removeEventListener('change', detectChange);
			} catch (e1) {
				try {
					// shitty Safari
					darkMediaQuery.removeListener(detectChange);
				} catch (e2) {
					console.error(e2);
				}
			}
		}
	}, []);
};

export const useSimpleFilter = (items, filter, dependencies = []) => {
	const [ data, setData ] = useState(items);
	const [ filtered, setFiltered ] = useState(items);

	useEffect(() => {
		setData(items);
		setFiltered(items);
	}, dependencies);

	const doFilter = (value) => {
		setFiltered(data.filter((d) => {
			return filter(d,value);
		}));
	};

	return [ filtered, doFilter ];
};

export const useKeyEvents = (events, listen = 'keydown', dependencies = []) => {
	const handleKeyDown = (e) => {
		if(events[e.keyCode] == undefined) return;
		//e.preventDefault();
		events[e.keyCode](e);
	};

	useEffect(() => {
		document.addEventListener(listen, handleKeyDown);

		return () => {
			document.removeEventListener(listen, handleKeyDown);
		};
	}, dependencies);
};

export const useEscEnter = (element,esc,enter,enabled,value) =>{

	const handleKeyDown = (e) => {
		if(e.keyCode == 27) {
			esc(e);
		}

		if(e.keyCode == 13) {
			enter(e);
		}
	};

	useEffect(() => {
		if (!enabled)return;
		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [enabled,value]);
}

export const useImageLoader = (images, cb) => {
	const pending = useRef(images.length);
	const loaded = useRef({});
	const load = (image, cb) => {
		let img = new window.Image();
		img.crossOrigin = 'Anonymous';
		img.src = image;
		img.onload = cb;
		return img;
	}
	

	useEffect(() => {
		pending.current = images.length;
		const to_delete = JSON.parse(JSON.stringify(loaded.current))

		if(images.length == 0) {
			cb(loaded.current);
			return;
		}
		images.map((image) => {
			if(!isNotEmpty(image,'url') || loaded.current[image.name]!=undefined && loaded.current[image.name].img.currentSrc == image.url) {
				delete to_delete[image.name];
				pending.current--;
				if (pending.current == 0) {
					if (Object.keys(to_delete).length > 0) {
						Object.keys(to_delete).map(file=>{
							delete loaded.current[file];
						})
					}
					cb(loaded.current);
				}
				else {
					return;
				}
			}
			const img = load(image.url, () => {
				if (loaded.current[image.name] != undefined) {
					if (!Array.isArray(loaded.current[image.name].id) && loaded.current[image.name].id != image.id) {
						loaded.current[image.name].id = [loaded.current[image.name].id];
					}
					if (Array.isArray(loaded.current[image.name].id) && !loaded.current[image.name].id.includes(image.id)) {
						loaded.current[image.name].id.push(image.id);
					}
				}
				else {
					loaded.current[image.name] = {
						img,
						id:image.id,
						group:image?.group,
						fit:image.fit,
						w:img.width,
						h:img.height,
						use_full_size:image.use_full_size,
						get_average_color:image.get_average_color,
					};

					if (image?.index != undefined) {
						loaded.current[image.name].index = image.index;
					}
				}



				pending.current--;
				if (Array.isArray(to_delete[image.name]?.id)) {
					to_delete[image.name].id = to_delete[image.name].id.filter(id => (id != image.id));
				}
				else {
					delete to_delete[image.name];
				}
				if(pending.current == 0) {
					if (Object.keys(to_delete).length > 0) {
						Object.keys(to_delete).map(file=>{
							if (Array.isArray(to_delete[file]?.id)) {
								if (to_delete[file].id.length > 0) {
									loaded.current[file].id = loaded.current[file].id.filter(id=>(!to_delete[file].id.includes(id)));
								}
								return;
							}
							delete loaded.current[file];
						})
					}

					cb(loaded.current)
				}
			})
		});

	}, [ images ]);
};

export const useClickOutside = (ref,action) => {
	useEffect(() => {
		/**
		 * Alert if clicked on outside of element
		 */
		const handleClickOutside = (event) => {
			if(ref.current && !ref.current.contains(event.target)) {
				action();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [ ref ]);
};


export const useAutoImage = ({auto_url,auto_type,image,...props},deps) => {

	const [state,dispatch] = useReducer((state,action)=>{
		switch (action.type) {
			case 'UPLOADING':
				return {...state,state:'UPLOADING'};
			case 'FINISHED':
				return {...state,state:'IDLE'};
			default:
				return state;
		}
	},{
		state:'IDLE'
	});

	useEffect(()=>{
		//debugger;

		if (deps[0] == undefined || !deps[0]) return;

		const value = props?.value ?? props?.model[props.name];
		
		if(auto_url != undefined) {
			let data = { image:deps[0], type:auto_type };
			if(value != undefined) {
				data.name = value.split('/').slice(-1)[0];
			} 	
			dispatch({type:'UPLOADING'});
	
			if(props?._size) {
				if(props._size.fit != undefined) data.fit = props._size.fit;
				if(props._size.width != undefined) data.width = props._size.width;
				if(props._size.height != undefined) data.height = props._size.height;
			}
			
			$.post(auto_url, data, (response) => {
				if(response.status) {
					dispatch({type:'FINISHED'});
					let full = `full_${props.name}`;
	
					if (props.changeMulti) {
						props.changeMulti({
							[props.name]:response.name,
							[full]:response.full
						});
						return ;
					} 
					
					if (props.change != undefined) {
						props.change(props.name,response.name ?? "", true);
						props.change(`full_${props.name}`, response.full ?? '');
					}
				}
			}, 'JSON');
		}

	},deps)

	return [state.state];

}